import { createSlice, createAction } from '@reduxjs/toolkit';

const initialState = {
  selectedProducts: [],
  deleteModalOpen: false,
  form: {
    formData: {
      name: '',
      description: '',
      productType: 'designer',
      isDigitalOnline: false,
      isLocalService: false,
      serviceRadius: 10,
      stock: 1,
      price: '',
      currency: 'USD', // Default currency
      photos: [],
      coverImageIndex: 0,
      generatedProductPhoto: null,
      historyGeneratedProductPhotos: [],
      forSell: false,
      forRent: false,
      openToSwap: false,
      openToPickUp: false,
      openToShipping: false,
      pickUpAddress: null,
      selectedAddressId: '',
      shippingAddress: null,
      selectedShippingAddressId: '',
      useShippingForPickup: false,
      rentPriceAndTime: [],
      requireDeposit: false,
      rentDeposit: '',
      category: '',
      size: '',
      customSizeDetails: '',
      color: '',
      condition: '',
      material: '',
      brand: '',
      parcelInfo: {
        length: '',
        width: '',
        height: '',
        weight: '',
        units: { distance: 'in', mass: 'lb' }
      },
      isConcept: true,
      isVirtual: false,
      isAiGenerated: false,
      is3dModel: false,
      hasVirtualCounterpart: false,
      expectedCurrencyForOrigin: null,
      shippingInsuranceRequired: false,
      shippingInsuranceCoverage: '',
      acceptAutoExtension: true,
      returnWindowDays: 7,
      itemValue: '',
      licensingEnabled: true,
      licensingType: 'free',
      licensingPrice: 12,
      allowDerivatives: false,
      licensingScope: {
        virtualTryOn: true,
        arPlacement: true,
        aiInspiration: false,
        socialSharing: true
      },
      // Custom request settings
      acceptCustomRequests: false,
      customRequestConfig: {
        rentalOffer: {
          enabled: true,
          minPrice: '',
          shortestPeriod: 3,
          shortestPeriodUnit: 'day',
        },
        commission: {
          enabled: true,
          startingPrice: '',
          leadTime: 2,
          leadTimeUnit: 'week',
        },
      },
    },
    editingId: null,
    fieldErrors: {
      name: '',
      description: '',
      productType: '',
      stock: '',
      price: '',
      currency: '',
      photos: '',
      category: '',
      size: '',
      color: '',
      condition: '',
      pickUpAddress: '',
      deliveryOptions: '',
      rentPriceAndTime: '',
      rentDeposit: '',
      material: '',
      brand: '',
      serviceRadius: '',
      itemValue: '',
      returnWindowDays: '',
      parcelInfo: {}
    }
  }
};

// Action to use outside of slice (used by product API)
export const resetSelectedProducts = createAction('listing/resetSelectedProducts');

// Helper function to prepare form data for submission to API
export const prepareFormDataForSubmit = (formData, userId) => {
  // Get product type for conditional processing
  const isVirtualFashion = formData.productType === 'virtual';
  const isOthersType = formData.productType === 'others';
  const isDigitalOnline = isOthersType && formData.isDigitalOnline;
  const isLocalService = isOthersType && formData.isLocalService;

  // Handle simplified product types (virtual or digital online) with similar behavior
  const isSimplifiedProduct = isVirtualFashion || isDigitalOnline;

  // Create the base data object
  const data = {
    name: formData.name,
    description: formData.description,
    productType: formData.productType,
    isDigitalOnline: formData.isDigitalOnline || false,
    isLocalService: formData.isLocalService || false,
    serviceRadius: formData.serviceRadius || 10,

    // ALWAYS include a category regardless of product type
    // This is a workaround for Strapi v3's entity validation
    category: formData.category || 'other',

    // Only include size and color for non-simplified products
    ...(!isSimplifiedProduct && !isLocalService ? {
      size: formData.size,
      customSizeDetails: formData.size === 'custom' ? formData.customSizeDetails : '',
      color: formData.color,
    } : {}),

    // Handle condition: required for sharing, optional otherwise
    ...(formData.productType === 'sharing' || (formData.condition && formData.condition !== '') ? {
      condition: formData.condition
    } : {}),

    // Material is optional - only include if it's not an empty string or null
    ...(formData.material && formData.material !== '' ? {
      material: formData.material
    } : {}),

    // Brand is optional - only include if it's not an empty string or null
    ...(formData.brand && formData.brand !== '' ? {
      brand: formData.brand
    } : {}),

    // For simplified products set stock to high value automatically
    stock: isSimplifiedProduct || isLocalService ? 999 : (parseInt(formData.stock) || 1),

    // For virtual fashion and digital/online, always include price
    // For others, only include price if the item is for sale
    ...(isSimplifiedProduct || formData.forSell ? {
      price: parseInt(formData.price) || 0,
      currency: formData.currency || 'USD'
    } : {
      price: 0,
      currency: formData.currency || 'USD'
    }),

    // Filter out any legacy isProductPhoto items from photos array and ensure we send IDs
    photos: (formData.photos || []).filter(p => !p.isProductPhoto).map(p => p?.id || p),
    coverImageIndex: Math.max(0, formData.coverImageIndex || 0), // Ensure coverImageIndex is never negative

    // AI-generated product photo stored as separate field (send file ID)
    generatedProductPhoto: formData.generatedProductPhoto?.id || formData.generatedProductPhoto || null,

    // History of previously generated product photos (send file IDs)
    historyGeneratedProductPhotos: (formData.historyGeneratedProductPhotos || []).map(p => p?.id || p).filter(Boolean),

    isVirtual: !!formData.isVirtual,
    hasVirtualCounterpart: !!formData.hasVirtualCounterpart,
    isAiGenerated: !!formData.isAiGenerated,
    is3dModel: !!formData.is3dModel,

    shippingInsuranceRequired: !!formData.shippingInsuranceRequired,
    shippingInsuranceCoverage: parseFloat(formData.shippingInsuranceCoverage) || 0,

    // For simplified products, force forSell to true and other options to false
    // For local services, force forRent to true and other options to false
    ...(isSimplifiedProduct ? {
      forSell: true,
      forRent: false,
      openToSwap: false,
      openToPickUp: false,
      openToShipping: false,
      isConcept: false,
    } : isLocalService ? {
      forSell: false,
      forRent: true,
      openToSwap: false,
      openToPickUp: true,  // Local services need pickup address for service location
      openToShipping: false,
    } : {
      forSell: formData.forSell,
      forRent: formData.forRent,
      openToSwap: formData.openToSwap,
      openToPickUp: formData.openToPickUp,
      openToShipping: formData.openToShipping,
      isConcept: formData.isConcept !== undefined ? formData.isConcept : (!formData.forSell && !formData.forRent && !formData.openToSwap && !formData.isVirtual),
    }),

    // Handle pickup address relation correctly - needed for both physical pickup and local services
    ...(((!isSimplifiedProduct && formData.openToPickUp) || isLocalService) ? {
      pickupAddress: formData.useShippingForPickup && formData.shippingAddress
        ? (formData.shippingAddress.id || formData.shippingAddress)
        : (formData.pickUpAddress ? (formData.pickUpAddress.id || formData.pickUpAddress) : formData.selectedAddressId),
      useShippingForPickup: formData.useShippingForPickup || false
    } : {}),

    // Handle shipping address relation if needed
    ...(!isSimplifiedProduct && formData.openToShipping ? {
      shippingAddress: formData.shippingAddress
        ? (formData.shippingAddress.id || formData.shippingAddress)
        : null
    } : {}),

    // Include parcel information if shipping is enabled
    ...(!isSimplifiedProduct && formData.openToShipping && formData.parcelInfo ? {
      parcelInfo: {
        length: parseFloat(formData.parcelInfo.length) || 0,
        width: parseFloat(formData.parcelInfo.width) || 0,
        height: parseFloat(formData.parcelInfo.height) || 0,
        weight: parseFloat(formData.parcelInfo.weight) || 0,
        units: formData.parcelInfo.units || { distance: 'in', mass: 'lb' }
      }
    } : {}),

    // Include rental info if item is for rent or is a local service
    ...(((formData.forRent && !isSimplifiedProduct) || isLocalService) ? {
      rentPriceAndTime: formData.rentPriceAndTime.map(rent => ({
        ...rent,
        price: parseInt(rent.price) || 0,
        currency: rent.currency || formData.currency || 'USD'
      })),
      rentDeposit: formData.requireDeposit ? (parseInt(formData.rentDeposit) || 0) : null,
      acceptAutoExtension: formData.acceptAutoExtension !== false,
      returnWindowDays: formData.acceptAutoExtension === false ? (parseInt(formData.returnWindowDays) || 7) : 7
    } : {
      rentPriceAndTime: [],
      rentDeposit: null
    }),

    // Item value — caps total charges (late fees + maintenance + deposit). Uses sale price when forSell is true.
    ...((formData.forRent || formData.forSell) ? {
      itemValue: formData.forSell && formData.price ? parseInt(formData.price) : (parseInt(formData.itemValue) || 0)
    } : {}),

    licensingEnabled: !!formData.licensingEnabled,
    licensingType: formData.licensingType || 'free',
    licensingPrice: parseFloat(formData.licensingPrice) || 0,
    licensingScope: {
      virtualTryOn: true,
      arPlacement: true,
      socialSharing: true,
      ...(formData.licensingScope || {}),
      aiInspiration: !!formData.allowDerivatives
    },

    // Custom request settings
    acceptCustomRequests: !!formData.acceptCustomRequests,
    customRequestConfig: formData.acceptCustomRequests ? {
      rentalOffer: {
        enabled: !!formData.customRequestConfig?.rentalOffer?.enabled,
        minPrice: parseInt(formData.customRequestConfig?.rentalOffer?.minPrice) || 0,
        shortestPeriod: parseInt(formData.customRequestConfig?.rentalOffer?.shortestPeriod) || 3,
        shortestPeriodUnit: formData.customRequestConfig?.rentalOffer?.shortestPeriodUnit || 'day',
      },
      commission: {
        enabled: !!formData.customRequestConfig?.commission?.enabled,
        startingPrice: parseInt(formData.customRequestConfig?.commission?.startingPrice) || 0,
        leadTime: parseInt(formData.customRequestConfig?.commission?.leadTime) || 2,
        leadTimeUnit: formData.customRequestConfig?.commission?.leadTimeUnit || 'week',
      },
    } : null,

    users_permissions_user: userId
  };

  // Log the data being sent to the API for debugging

  return data;
};

const listingSlice = createSlice({
  name: 'listing',
  initialState,
  reducers: {
    toggleProductSelection: (state, action) => {
      const productId = action.payload;
      const productIndex = state.selectedProducts.indexOf(productId);

      if (productIndex === -1) {
        // If product is not selected, add it
        state.selectedProducts.push(productId);
      } else {
        // If product is already selected, remove it
        state.selectedProducts.splice(productIndex, 1);
      }
    },
    toggleSelectAll: (state, action) => {
      const productIds = action.payload;

      if (state.selectedProducts.length === productIds.length) {
        // If all products are selected, deselect all
        state.selectedProducts = [];
      } else {
        // If some or none are selected, select all
        state.selectedProducts = [...productIds];
      }
    },
    clearSelectedProducts: (state) => {
      state.selectedProducts = [];
    },
    setDeleteModalOpen: (state, action) => {
      state.deleteModalOpen = action.payload;
    },
    setExpectedCurrencyForOrigin: (state, action) => {
      state.form.formData.expectedCurrencyForOrigin = action.payload;
    },
    updateFormData: (state, action) => {
      // First merge the new data
      const newFormData = {
        ...state.form.formData,
        ...action.payload
      };

      // Check if productType is being changed to 'virtual'
      if (action.payload.productType === 'virtual' &&
        state.form.formData.productType !== 'virtual') {
        // Set appropriate defaults for virtual fashion
        newFormData.forSell = true;
        newFormData.forRent = false;
        newFormData.openToSwap = false;
        newFormData.openToPickUp = false;
        newFormData.openToShipping = false;
        newFormData.stock = 999; // Set a high stock value for virtual products
      }

      // Check if we're changing to a digital/online product
      if (action.payload.productType === 'others' &&
        action.payload.isDigitalOnline === true &&
        !(state.form.formData.productType === 'others' && state.form.formData.isDigitalOnline === true)) {
        // Set appropriate defaults for digital/online products - similar to virtual products
        newFormData.forSell = true;
        newFormData.forRent = false;
        newFormData.openToSwap = false;
        newFormData.openToPickUp = false;
        newFormData.openToShipping = false;
        newFormData.stock = 999; // Set a high stock value
        newFormData.isLocalService = false; // Can't be both digital and local
      }

      // Check if we're changing to a local service
      if (action.payload.productType === 'others' &&
        action.payload.isLocalService === true &&
        !(state.form.formData.productType === 'others' && state.form.formData.isLocalService === true)) {
        // Set appropriate defaults for local services
        newFormData.forSell = false;
        newFormData.forRent = true;
        newFormData.openToSwap = false;
        newFormData.openToPickUp = true; // Need pickup address for service location
        newFormData.openToShipping = false;
        newFormData.stock = 999; // Set a high stock value for service
        newFormData.isDigitalOnline = false; // Can't be both digital and local

        // Initialize service radius with a default value
        newFormData.serviceRadius = newFormData.serviceRadius || 10;

        // Initialize rentPriceAndTime with an hourly rate if it's empty
        if (!newFormData.rentPriceAndTime || newFormData.rentPriceAndTime.length === 0) {
          newFormData.rentPriceAndTime = [{
            number: 1,
            option: 'hour',
            price: '',
            currency: newFormData.currency || 'USD'
          }];
        }
      }

      // Auto-sync itemValue from sale price when forSell is enabled
      if (newFormData.forSell && newFormData.price) {
        newFormData.itemValue = newFormData.price;
      }
      // If price changes while forSell is on, keep itemValue in sync
      if (action.payload.price !== undefined && newFormData.forSell) {
        newFormData.itemValue = action.payload.price;
      }

      // Update state with potentially modified data
      state.form.formData = newFormData;
    },
    updateFieldErrors: (state, action) => {
      state.form.fieldErrors = {
        ...state.form.fieldErrors,
        ...action.payload
      };
    },
    setEditingId: (state, action) => {
      state.form.editingId = action.payload;
    },
    resetForm: (state) => {
      state.form = initialState.form;
    },
    initializeFormWithProduct: (state, action) => {
      const product = action.payload;

      // Update form data with product details
      state.form.formData = {
        ...state.form.formData,
        id: product.id,
        name: product.name || '',
        description: product.description || '',
        productType: product.productType || '',
        isDigitalOnline: product.isDigitalOnline || false,
        isLocalService: product.isLocalService || false,
        serviceRadius: product.serviceRadius || 10,
        category: product.category || '',
        size: product.size || '',
        customSizeDetails: product.customSizeDetails || '',
        color: product.color || '',
        condition: product.condition || '',
        material: product.material || '',
        brand: product.brand || '',
        price: product.price || '',
        currency: product.currency || 'USD',
        stock: product.stock !== undefined && product.stock !== null ? product.stock : 1,
        photos: (product.photos || []).filter(p => !p.isProductPhoto),
        coverImageIndex: Math.max(0, product.coverImageIndex || 0),
        generatedProductPhoto: product.generatedProductPhoto || null,
        historyGeneratedProductPhotos: product.historyGeneratedProductPhotos || [],
        forSell: !!product.forSell,
        forRent: !!product.forRent,
        openToSwap: !!product.openToSwap,
        openToPickUp: !!product.openToPickUp,
        openToShipping: !!product.openToShipping,
        isConcept: product.isConcept !== undefined ? !!product.isConcept : (!product.forSell && !product.forRent && !product.openToSwap && !product.isVirtual),
        isVirtual: !!product.isVirtual,
        hasVirtualCounterpart: !!product.hasVirtualCounterpart,
        isAiGenerated: !!product.isAiGenerated,
        is3dModel: !!product.is3dModel,
        rentPriceAndTime: (product.rentPriceAndTime || []).map(rentPrice => ({
          ...rentPrice,
          currency: rentPrice.currency || product.currency || 'USD'
        })),
        requireDeposit: !!product.rentDeposit,
        rentDeposit: product.rentDeposit || '',

        // Handle pickup address relation correctly
        pickUpAddress: product.pickupAddress || null,
        selectedAddressId: product.pickupAddress?.id || (typeof product.pickupAddress === 'string' ? product.pickupAddress : null),

        // Handle shipping address relation correctly
        shippingAddress: product.shippingAddress || null,
        selectedShippingAddressId: product.shippingAddress?.id || (typeof product.shippingAddress === 'string' ? product.shippingAddress : null),
        useShippingForPickup: !!product.useShippingForPickup,

        // Handle parcel information
        parcelInfo: {
          length: product.parcelInfo?.length || '',
          width: product.parcelInfo?.width || '',
          height: product.parcelInfo?.height || '',
          weight: product.parcelInfo?.weight || '',
          units: product.parcelInfo?.units || { distance: 'in', mass: 'lb' }
        },

        // Rental extension settings
        acceptAutoExtension: product.acceptAutoExtension !== undefined ? !!product.acceptAutoExtension : true,
        returnWindowDays: product.returnWindowDays || 7,
        itemValue: product.itemValue || (product.forSell && product.price ? product.price : ''),

        shippingInsuranceRequired: !!product.shippingInsuranceRequired,
        shippingInsuranceCoverage: product.shippingInsuranceCoverage || '',

        licensingEnabled: !!product.licensingEnabled,
        licensingType: product.licensingType || 'free',
        licensingPrice: product.licensingPrice || 12,
        licensingScope: product.licensingScope || {
          virtualTryOn: true,
          arPlacement: true,
          aiInspiration: false,
          socialSharing: true
        },
        allowDerivatives: product.licensingScope ? !!product.licensingScope.aiInspiration : false,

        // Custom request settings
        acceptCustomRequests: !!product.acceptCustomRequests,
        customRequestConfig: product.customRequestConfig || {
          rentalOffer: {
            enabled: true,
            minPrice: '',
            shortestPeriod: 3,
            shortestPeriodUnit: 'day',
          },
          commission: {
            enabled: true,
            startingPrice: '',
            leadTime: 2,
            leadTimeUnit: 'week',
          },
        },
      };

      // Log the pickup address for debugging

      // Clear any field errors
      state.form.fieldErrors = initialState.form.fieldErrors;
    },
    normalizeFormData: (state) => {
      // Convert empty strings to 0 for rental-related fields
      state.form.formData = {
        ...state.form.formData,
        rentDeposit: state.form.formData.rentDeposit === '' ? 0 : state.form.formData.rentDeposit,
      };
    }
  },
  extraReducers: (builder) => {
    // Handle external actions
    builder
      .addCase(resetSelectedProducts, (state) => {
        state.selectedProducts = [];
      });
  }
});

export const {
  toggleProductSelection,
  toggleSelectAll,
  clearSelectedProducts,
  setDeleteModalOpen,
  setExpectedCurrencyForOrigin,
  updateFormData,
  updateFieldErrors,
  setEditingId,
  resetForm,
  initializeFormWithProduct,
  normalizeFormData
} = listingSlice.actions;

export default listingSlice.reducer; 